/**
 * SM Feedback Popup
 * Lightweight feedback collection for logged-in customers
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    TIMEOUT_MS: 75000, // 75 seconds
    NOT_NOW_DAYS: 7,
    SUBMITTED_DAYS: 30,
    SESSION_ID_KEY: 'sm_session_id',
    SUBMITTED_KEY: 'sm_feedback_submitted',
    NOT_NOW_KEY: 'sm_feedback_not_now',
    ENDPOINT: '/apps/referral/feedback'
  };

  // State
  let popupShown = false;
  let timeoutId = null;
  let exitIntentTriggered = false;

  // DOM elements
  const popup = document.getElementById('sm-feedback-popup');
  const form = document.getElementById('sm-feedback-form');
  const otherInput = document.getElementById('sm-feedback-other-input');
  const reasonOtherInput = document.getElementById('sm-feedback-reason-other');
  const notNowBtn = form?.querySelector('[data-action="not-now"]');

  // Early exit if popup doesn't exist or already shown/submitted
  if (!popup || !form) {
    return;
  }

  // Check if popup should be shown
  function shouldShowPopup() {
    // Check if already submitted (30 days)
    const submittedData = localStorage.getItem(CONFIG.SUBMITTED_KEY);
    if (submittedData) {
      try {
        const data = JSON.parse(submittedData);
        const daysSince = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
        if (daysSince < CONFIG.SUBMITTED_DAYS) {
          return false;
        }
        // Expired, remove it
        localStorage.removeItem(CONFIG.SUBMITTED_KEY);
      } catch (e) {
        localStorage.removeItem(CONFIG.SUBMITTED_KEY);
      }
    }

    // Check if "Not now" was clicked (7 days)
    const notNowData = localStorage.getItem(CONFIG.NOT_NOW_KEY);
    if (notNowData) {
      try {
        const data = JSON.parse(notNowData);
        const daysSince = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
        if (daysSince < CONFIG.NOT_NOW_DAYS) {
          return false;
        }
        // Expired, remove it
        localStorage.removeItem(CONFIG.NOT_NOW_KEY);
      } catch (e) {
        localStorage.removeItem(CONFIG.NOT_NOW_KEY);
      }
    }

    return true;
  }

  // Get or create session ID
  function getSessionId() {
    let sessionId = localStorage.getItem(CONFIG.SESSION_ID_KEY);
    if (!sessionId) {
      // Generate a simple session ID
      sessionId = 'sm_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
      try {
        localStorage.setItem(CONFIG.SESSION_ID_KEY, sessionId);
      } catch (e) {
        console.error('Failed to store session ID:', e);
      }
    }
    return sessionId;
  }

  // Translation fallback - since ar.json may exceed Shopify's 3,400 key limit
  // JavaScript translation ensures it works even if Liquid translations fail
  const translations = {
    ar: {
      title: 'لماذا لم تشتري؟',
      reason_not_now: 'ليس الآن',
      reason_not_found: 'لم أجد ما أريده',
      reason_price: 'مخاوف السعر',
      reason_need_time: 'أحتاج مزيدًا من الوقت للتفكير',
      reason_other: 'أخرى',
      other_label: 'يرجى التوضيح:',
      other_placeholder: 'أخبرنا أكثر...',
      send: 'إرسال',
      not_now: 'الغاء',
      sending: 'جاري الإرسال...',
      error_message: 'فشل إرسال الملاحظات. يرجى المحاولة مرة أخرى.'
    }
  };

  function translateText() {
    const isRTL = document.documentElement.dir === 'rtl' || 
                  document.documentElement.getAttribute('lang') === 'ar' ||
                  window.location.pathname.startsWith('/ar');
    
    if (!isRTL) return; // Only translate for Arabic
    
    const lang = 'ar';
    const t = translations[lang];
    if (!t) return;

    // Translate title
    const titleEl = popup?.querySelector('#sm-feedback-title');
    if (titleEl && /[\u0600-\u06FF]/.test(titleEl.textContent) === false) {
      titleEl.textContent = t.title;
    }

    // Translate radio options
    const reasonLabels = popup?.querySelectorAll('.sm-feedback-option span');
    if (reasonLabels) {
      reasonLabels.forEach(label => {
        const input = label.parentElement?.querySelector('input[type="radio"]');
        if (input && !/[\u0600-\u06FF]/.test(label.textContent)) {
          const value = input.value;
          if (value === 'not_now') label.textContent = t.reason_not_now;
          else if (value === 'not_found') label.textContent = t.reason_not_found;
          else if (value === 'price') label.textContent = t.reason_price;
          else if (value === 'need_time') label.textContent = t.reason_need_time;
          else if (value === 'other') label.textContent = t.reason_other;
        }
      });
    }

    // Translate "other" input label and placeholder
    const otherLabel = popup?.querySelector('label[for="sm-feedback-reason-other"]');
    if (otherLabel && !/[\u0600-\u06FF]/.test(otherLabel.textContent)) {
      otherLabel.textContent = t.other_label;
    }
    const otherInput = popup?.querySelector('#sm-feedback-reason-other');
    if (otherInput && !/[\u0600-\u06FF]/.test(otherInput.placeholder || '')) {
      otherInput.placeholder = t.other_placeholder;
    }

    // Translate buttons
    const sendBtn = form?.querySelector('button[type="submit"]');
    if (sendBtn && sendBtn.getAttribute('data-send-text') && !/[\u0600-\u06FF]/.test(sendBtn.textContent)) {
      sendBtn.setAttribute('data-send-text', t.send);
      sendBtn.setAttribute('data-sending-text', t.sending);
      if (sendBtn.textContent.trim() === 'Send' || sendBtn.textContent.trim() === 'Why didn\'t you buy?') {
        sendBtn.textContent = t.send;
      }
    }
    const notNowBtn = form?.querySelector('button[data-action="not-now"]');
    if (notNowBtn && !/[\u0600-\u06FF]/.test(notNowBtn.textContent)) {
      notNowBtn.textContent = t.not_now;
    }

    // Translate error message
    const errorEl = popup?.querySelector('[data-error-message]');
    if (errorEl && errorEl.getAttribute('data-error-message') && !/[\u0600-\u06FF]/.test(errorEl.getAttribute('data-error-message'))) {
      errorEl.setAttribute('data-error-message', t.error_message);
    }
  }

  // Show popup
  function showPopup() {
    if (popupShown || !shouldShowPopup()) {
      return;
    }

    popupShown = true;
    popup.setAttribute('aria-hidden', 'false');
    popup.style.display = 'flex';

    // Set RTL if needed
    const isRTL = document.documentElement.dir === 'rtl';
    if (isRTL) {
      popup.setAttribute('dir', 'rtl');
    }
    
    // Apply JavaScript translation fallback
    translateText();

    // Focus trap (basic)
    const firstFocusable = form.querySelector('input[type="radio"]');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  // Hide popup
  function hidePopup() {
    popupShown = false;
    popup.setAttribute('aria-hidden', 'true');
    popup.style.display = 'none';
  }

  // Handle "other" option toggle
  function handleReasonChange(e) {
    if (e.target.type === 'radio') {
      if (e.target.value === 'other') {
        otherInput.style.display = 'flex';
        reasonOtherInput.required = true;
        setTimeout(() => reasonOtherInput.focus(), 100);
      } else {
        otherInput.style.display = 'none';
        reasonOtherInput.required = false;
        reasonOtherInput.value = '';
      }
    }
  }

  // Handle form submission
  async function handleSubmit(e) {
    e.preventDefault();

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn.disabled) {
      return;
    }

    // Get selected reason
    const reasonInput = form.querySelector('input[name="reason"]:checked');
    if (!reasonInput) {
      return;
    }

    const reason = reasonInput.value;
    const reasonOther = reason === 'other' ? reasonOtherInput.value.trim() : '';

    // Validate "other" reason
    if (reason === 'other' && !reasonOther) {
      reasonOtherInput.focus();
      return;
    }

    // Disable submit button
    submitBtn.disabled = true;
    const sendingText = submitBtn.getAttribute('data-sending-text');
    if (sendingText) {
      submitBtn.textContent = sendingText;
    } else {
      submitBtn.textContent = 'Sending...';
    }

    // Get customer email from popup data attribute
    const customerEmail = popup.getAttribute('data-customer-email') || '';

    // Prepare payload
    const payload = {
      reason: reason,
      page_url: window.location.href,
      session_id: getSessionId()
    };

    if (reasonOther) {
      payload.reason_other = reasonOther;
    }

    if (customerEmail) {
      payload.email = customerEmail;
    }

    try {
      // Build proxy URL
      let proxyUrl = CONFIG.ENDPOINT;
      
      let response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Handle 404 before trying to parse JSON
      if (response.status === 404) {
        throw new Error('Feedback endpoint not found (404). App Proxy at /apps/support/feedback is not configured in Shopify Partner Dashboard.');
      }

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Expected JSON but received ${contentType || 'unknown type'}. Status: ${response.status}. Response: ${text.substring(0, 100)}`);
      }

      if (data.ok === true) {
        // Success - mark as submitted
        const submittedData = {
          timestamp: Date.now()
        };
        try {
          localStorage.setItem(CONFIG.SUBMITTED_KEY, JSON.stringify(submittedData));
        } catch (e) {
          console.error('Failed to store submitted status:', e);
        }

        hidePopup();
      } else {
        // Error
        console.error('Feedback submission failed:', data.error || 'Unknown error');
        submitBtn.disabled = false;
        const sendText = submitBtn.getAttribute('data-send-text');
        submitBtn.textContent = sendText || 'Send';
        const errorElement = document.querySelector('[data-error-message]');
        const errorMsg = errorElement ? errorElement.getAttribute('data-error-message') : 'Failed to submit feedback. Please try again.';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      submitBtn.disabled = false;
      const sendText = submitBtn.getAttribute('data-send-text');
      submitBtn.textContent = sendText || 'Send';
      const errorElement = document.querySelector('[data-error-message]');
      const errorMsg = errorElement ? errorElement.getAttribute('data-error-message') : 'Failed to submit feedback. Please try again.';
      alert(errorMsg);
    }
  }

  // Handle "Not now" button
  function handleNotNow() {
    const notNowData = {
      timestamp: Date.now()
    };
    try {
      localStorage.setItem(CONFIG.NOT_NOW_KEY, JSON.stringify(notNowData));
    } catch (e) {
      console.error('Failed to store not now status:', e);
    }
    hidePopup();
  }

  // Handle escape key
  function handleEscape(e) {
    if (e.key === 'Escape' && popupShown) {
      hidePopup();
    }
  }

  // Handle exit intent (mouse leaves top)
  function handleExitIntent(e) {
    if (exitIntentTriggered || popupShown) {
      return;
    }

    // Check if mouse is moving upward and is near the top
    if (e.clientY <= 0 && shouldShowPopup()) {
      exitIntentTriggered = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      showPopup();
    }
  }

  // Initialize
  function init() {
    if (!shouldShowPopup()) {
      return;
    }

    // Apply JavaScript translation fallback on init (in case popup is already in DOM)
    translateText();

    // Set up event listeners
    form.addEventListener('change', handleReasonChange);
    form.addEventListener('submit', handleSubmit);
    
    if (notNowBtn) {
      notNowBtn.addEventListener('click', handleNotNow);
    }

    // Close on overlay click
    const overlay = popup.querySelector('.sm-feedback-overlay');
    if (overlay) {
      overlay.addEventListener('click', hidePopup);
    }

    // Escape key handler
    document.addEventListener('keydown', handleEscape);

    // Exit intent handler
    document.addEventListener('mouseleave', handleExitIntent);

    // Timeout handler (75 seconds)
    timeoutId = setTimeout(() => {
      if (!popupShown && shouldShowPopup()) {
        showPopup();
      }
    }, CONFIG.TIMEOUT_MS);
  }

  // Run initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

